import Check from '@mui/icons-material/Check';
import FlagOutlined from '@mui/icons-material/FlagOutlined';
import MoreHoriz from '@mui/icons-material/MoreHoriz';
import WarningAmber from '@mui/icons-material/WarningAmber';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {
  deleteV1CompaniesCompanyIdTicketsId,
  TicketState,
  TicketStateEvent,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useRef, useState } from 'react';

import DataGridPager from '~components/DataGrid/DataGridPager';
import actionsColumn from '~components/DataTable/actionsColumn';
import DataTable from '~components/DataTable/DataTable';
import { DataTableColumn } from '~components/DataTable/dataTableColumn';
import { Drawer } from '~components/Drawer/src/ui';
import { SimpleMenu } from '~components/Menu/SimpleMenu';
import { MAX_API_PAGE_SIZE } from '~constants/consts';
import useCursorPagination from '~hooks/useCursorPagination/useCursorPagination';
import { Job } from '~hooks/useJob/models';
import { useJob } from '~hooks/useJob/useJob';
import { Ticket, TicketUpdateInput } from '~hooks/useTickets/models';
import { useTickets } from '~hooks/useTickets/useTickets';
import { DateRangeFilter } from '~pages/Approvals/ApprovalsComponents/DateRangeFilter';
import connection from '~services/connectionModule';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { Nullable } from '~types/Nullable';
import { dateFormat } from '~utils/dateTime';
import { getChangedScaleTicketIds } from '~utils/tickets/scale-ticket-fields';
import { canEditTicket } from '~utils/tickets/ticket-utils';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { TicketFormDTO } from '../ApprovalsComponents/ticketFormSchema';
import { TicketDetails } from './TicketDetails';
import { TicketTabFilter } from './TicketTabFilter';
import { TabFilter } from './types';
import { TAB_SERVER_FILTERS } from './utils';

export const TicketsReviewDataGrid = observer(() => {
  const { ticketsStore, toasterStore, jobStore, userStore } = useStores();
  const {
    getTickets,
    updateState,
    updateTicketById,
    deleteTicketImageById,
    uploadTicketImageById,
  } = useTickets();
  const [selectedTicket, setSelectedTicket] = useState<{
    isDrawerOpen: boolean;
    job?: Job;
    ticket?: Ticket;
  }>({ isDrawerOpen: false });
  const { getJobById } = useJob();
  const companyId = getEffectiveUserCompanyId(userStore);

  // This is to make sure the selected ticket is always upto date from the
  // Store to prevent using a stale ticket data in the drawer.
  // Example: if a user approves a ticket from the datagrid and immediately opens the drawer,
  // We want to make sure the new states is reflected in the drawer.
  // Only way to show the updated ticket is to always fetch it from the store.
  useEffect(() => {
    if (selectedTicket.ticket?.id) {
      const newSelectedTicket = ticketsStore.tickets.find((storedTicket) => {
        return storedTicket.id === selectedTicket.ticket?.id;
      });

      if (newSelectedTicket) {
        setSelectedTicket((state) => ({ ...state, ticket: newSelectedTicket }));
      }
    }
  }, [ticketsStore.tickets, selectedTicket.ticket?.id]);

  useEffect(() => {
    if (selectedTicket.isDrawerOpen) {
      const job = jobStore.jobs.find((job) => {
        return selectedTicket.ticket?.jobId === job.id;
      });

      if (job) {
        setSelectedTicket((state) => ({ ...state, job }));
      } else if (selectedTicket.ticket?.jobId) {
        getJobById(selectedTicket.ticket.jobId, companyId).then((associatedJob) => {
          setSelectedTicket((state) => ({ ...state, job: associatedJob }));
        });
      }
    }
  }, [
    companyId,
    jobStore.jobs,
    selectedTicket.isDrawerOpen,
    selectedTicket.ticket?.jobId,
  ]);

  // The server filters each tab, so a ticket that stops matching its tab leaves the page
  // only when the current page is reloaded.
  const reloadCurrentPageRef = useRef<() => Promise<void>>(async () => {});

  const doAction = async (id: string, action: TicketStateEvent) => {
    const res = await updateState({ companyId, event: action, id });
    toasterStore.push(alert(t('approvals.ticket_updated'), AlertTypes.success));
    await reloadCurrentPageRef.current();
    return res;
  };

  const doUpdateAction = async (id: string, data: TicketUpdateInput) => {
    const res = await updateTicketById({ companyId, id, data });
    toasterStore.push(alert(t('approvals.ticket_updated'), AlertTypes.success));
    await reloadCurrentPageRef.current();
    return res;
  };

  const saveTicket = async (ticketDto: TicketFormDTO) => {
    let ticket: Ticket | undefined = undefined;
    const isUpdate = Boolean(ticketDto.id);

    if (isUpdate) {
      ticket = await updateTicketById({
        companyId,
        id: String(ticketDto.id),
        data: {
          quantity: ticketDto.quantity,
          unitOfMeasure: ticketDto.unitOfMeasure,
          material: ticketDto.material.id ? { id: ticketDto.material.id } : undefined,
          ticketNumber: ticketDto.ticketNumber,
          imageUrl: ticketDto.imageUrl,
          ...getChangedScaleTicketIds(ticketDto, selectedTicket.ticket),
        },
      });
    }

    if (ticket) {
      toasterStore.push(alert(t('approvals.ticket_updated'), AlertTypes.success));
      setSelectedTicket((state) => ({ ...state, ticket }));
      await reloadCurrentPageRef.current();
    } else {
      throw new Error('Failed to update ticket.');
    }

    return ticket;
  };

  const handleFlagTicket = async (ticket: Ticket) => {
    ticket.flagged = !ticket.flagged;

    const updatedTicket = await updateTicketById({
      companyId,
      id: ticket.id,
      data: {
        flagged: ticket.flagged,
      },
    });

    toasterStore.push(alert(t('approvals.ticket_updated'), AlertTypes.success));
    setSelectedTicket((state) => ({ ...state, ticket: updatedTicket }));
    await reloadCurrentPageRef.current();

    return updatedTicket;
  };

  const [dateRangeFilterValue, setDateRangeFilterValue] = useState<string[] | null[]>([
    null,
    null,
  ]);
  const [tabFilter, setTabFilter] = useState<TabFilter>(TabFilter.MISSING);
  const filterParams = useMemo(
    () => ({
      ...(dateRangeFilterValue[0]
        ? { from_date: new Date(dateRangeFilterValue[0]).toISOString() }
        : {}),
      ...(dateRangeFilterValue[1]
        ? { to_date: new Date(dateRangeFilterValue[1]).toISOString() }
        : {}),
      ...TAB_SERVER_FILTERS[tabFilter],
    }),
    [dateRangeFilterValue, tabFilter],
  );

  const { isFetching, pagination, goToPage, reloadCurrentPage, startOver } =
    useCursorPagination<Ticket>({
      fetchPage: async ({ cursor, limit, link, signal }) => {
        const page = await getTickets({
          companyId,
          cursor,
          filterParams,
          limit,
          link,
          signal,
        });

        if (!signal.aborted) {
          ticketsStore.setTickets(page.data);
        }

        return page;
      },
      limit: MAX_API_PAGE_SIZE,
    });

  reloadCurrentPageRef.current = reloadCurrentPage;

  useEffect(() => {
    // A failed fetch must not leave the previous filter's tickets or cursors on screen.
    ticketsStore.setTickets([]);
    void startOver();
  }, [JSON.stringify(filterParams), companyId]);

  // The columns are memoized, so they call the latest handlers through a ref.
  const rowActionsRef = useRef({ doAction, doUpdateAction });
  rowActionsRef.current = { doAction, doUpdateAction };

  const columns: Array<DataTableColumn<Ticket>> = useMemo(
    () => [
      {
        field: 'ticketNumber',
        headerName: t('form_fields.ticket'),
        valueGetter: (ticket) => ticket.ticketNumber,
      },
      {
        field: 'ticketWarning',
        headerName: t('approvals.alert'),
        valueGetter: (ticket) => (ticket.missingInfo ? t('approvals.alert') : ''),
        renderCell: (ticket) => {
          if (!ticket.missingInfo) {
            return null;
          }

          const nodes = [];
          if (!ticket.imageUrl) {
            nodes.push(
              <div key="missing-imageUrl">{t('approvals.missing_image_warning')}</div>,
            );
          }
          if (!ticket.ticketNumber) {
            nodes.push(
              <div key="missing-ticketNumber">
                {t('approvals.missing_ticket_number_warning')}
              </div>,
            );
          }
          if (!ticket.jobId) {
            nodes.push(
              <div key="missing-jobId">{t('approvals.missing_job_warning')}</div>,
            );
          }
          if (!ticket.quantity) {
            nodes.push(
              <div key="missing-quantify">{t('approvals.missing_quantity_warning')}</div>,
            );
          }

          return (
            <Tooltip title={<>{nodes}</>}>
              <WarningAmber color="primary" />
            </Tooltip>
          );
        },
      },
      {
        field: 'ticketImage',
        headerName: t('approvals.image'),
        valueGetter: (ticket) => ticket.imageUrl,
        renderCell: (ticket) =>
          ticket.imageUrl ? (
            <Typography
              component="a"
              href={ticket.imageUrl}
              rel="noreferrer"
              sx={{ '&:hover': { textDecoration: 'underline' } }}
              target="_blank"
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              {t('approvals.view_image')}
            </Typography>
          ) : null,
      },
      {
        field: 'createdAtDate',
        headerName: t('form_fields.date'),
        valueGetter: (ticket) => dateFormat(ticket.createdAt),
      },
      {
        field: 'createdAtTime',
        headerName: t('form_fields.time'),
        valueGetter: (ticket) => dateFormat(ticket.createdAt, 'hh:mm A'),
      },
      {
        field: 'customerName',
        headerName: t('form_fields.customer'),
        valueGetter: (ticket) => ticket.customerName,
      },
      {
        field: 'projectName',
        headerName: t('form_fields.project'),
        valueGetter: (ticket) => ticket.projectName,
      },
      {
        field: 'friendlyJobId',
        headerName: t('form_fields.job'),
        valueGetter: (ticket) => ticket.friendlyJobId,
      },
      {
        field: 'materialName',
        headerName: t('form_fields.material'),
        valueGetter: (ticket) => ticket.materialName,
      },
      {
        field: 'quantity',
        headerName: t('form_fields.quantity'),
        valueGetter: (ticket) => ticket.quantity,
      },
      {
        field: 'pickupSiteName',
        headerName: `${t('form_fields.site')} A`,
        valueGetter: (ticket) => ticket.pickupSiteName,
      },
      {
        field: 'dropoffSiteName',
        headerName: `${t('form_fields.site')} B`,
        valueGetter: (ticket) => ticket.dropoffSiteName,
      },
      {
        field: 'vendorName',
        headerName: t('form_fields.vendor'),
        valueGetter: (ticket) => ticket.vendorName,
      },
      {
        field: 'equipmentName',
        headerName: t('form_fields.equipment'),
        valueGetter: (ticket) => ticket.equipmentName,
      },
      {
        field: 'source',
        headerName: t('form_fields.source'),
        valueGetter: (ticket) => ticket.source,
      },
      {
        field: 'flagged',
        headerName: t('form_fields.flagged'),
        valueGetter: (ticket) => (ticket.flagged ? t('form_fields.flagged') : ''),
        renderCell: (ticket) =>
          ticket.flagged ? <FlagOutlined color="primary" /> : null,
      },
      {
        field: 'status',
        headerName: t('form_fields.status'),
        minWidth: 125,
        valueGetter: (ticket) => ticket.state,
        renderCell: (ticket) => {
          const showApproval = ticket.state && canEditTicket(ticket.state);

          return showApproval ? (
            <Button
              variant="contained"
              color="success"
              startIcon={<Check />}
              onClick={(e) => {
                e.stopPropagation();
                rowActionsRef.current.doAction(ticket.id, TicketStateEvent.APPROVE);
              }}
            >
              {t('approvals.approve')}
            </Button>
          ) : (
            <Chip
              label={ticket.state}
              color={GetStateColor(ticket.state)}
              variant="outlined"
            />
          );
        },
      },
      actionsColumn((ticket) => {
        if (!canEditTicket(ticket.state)) {
          return null;
        }

        return (
          <SimpleMenu
            options={[
              {
                title: ticket.flagged
                  ? t('approvals.remove_flag')
                  : t('approvals.add_flag'),
                callBack: () =>
                  rowActionsRef.current.doUpdateAction(ticket.id, {
                    flagged: !ticket.flagged,
                  }),
              },
              {
                title: t('approvals.void_ticket'),
                callBack: () =>
                  rowActionsRef.current.doAction(ticket.id, TicketStateEvent.VOID),
              },
            ]}
          >
            <MoreHoriz />
          </SimpleMenu>
        );
      }),
    ],
    [],
  );

  return (
    <Box>
      <Box sx={{ pb: 2 }}>
        <TicketTabFilter
          value={tabFilter}
          onChange={(tab) => {
            setTabFilter(tab);
          }}
        />
      </Box>

      <DataTable
        id="tickets-review-grid"
        rows={ticketsStore.tickets}
        columns={columns}
        loading={isFetching}
        onRowClick={(ticket) => {
          setSelectedTicket((state) => ({ ...state, isDrawerOpen: true, ticket }));
        }}
        headerTrailingComponent={
          <Box sx={{ alignItems: 'center', display: 'flex', gap: 2 }}>
            <DateRangeFilter
              sx={{ minWidth: '220px' }}
              name="dates"
              values={dateRangeFilterValue}
              callBack={setDateRangeFilterValue}
            />
            <DataGridPager
              isFetching={isFetching}
              onNavigate={(link) => void goToPage(link)}
              pagination={pagination}
            />
          </Box>
        }
      />

      <Drawer
        anchor="right"
        onClose={() => setSelectedTicket((state) => ({ ...state, isDrawerOpen: false }))}
        open={selectedTicket.isDrawerOpen}
        variant="temporary"
      >
        <>
          {selectedTicket.job && (
            <TicketDetails
              job={selectedTicket.job}
              loadId={selectedTicket.ticket?.loadId}
              ticket={selectedTicket.ticket}
              onFlag={handleFlagTicket}
              onApprove={async (ticket) => {
                const approvedTicket = await doAction(
                  ticket.id,
                  TicketStateEvent.APPROVE,
                );

                if (approvedTicket) {
                  setSelectedTicket((state) => ({ ...state, ticket: approvedTicket }));
                }
              }}
              onRemoveImage={async (ticket) => {
                await deleteTicketImageById({ companyId, id: ticket.id });
                const newTicket = ticket;
                newTicket.imageUrl = '';
                setSelectedTicket((state) => ({ ...state, ticket: newTicket }));
                await reloadCurrentPageRef.current();
              }}
              onUploadImage={async (ticket, image) => {
                if (ticket) {
                  const updatedTicket = await uploadTicketImageById({
                    companyId,
                    id: ticket.id,
                    file: image,
                  });

                  if (updatedTicket) {
                    setSelectedTicket((state) => ({ ...state, ticket: updatedTicket }));
                    await reloadCurrentPageRef.current();
                  }
                }
              }}
              onChange={saveTicket}
              onClose={() => {
                setSelectedTicket((state) => ({ ...state, isDrawerOpen: false }));
              }}
              onDelete={async (ticket) => {
                try {
                  await deleteV1CompaniesCompanyIdTicketsId({
                    path: { 'company-id': companyId, id: ticket.id },
                  });
                  ticketsStore.deleteTicket(ticket.id);
                  setSelectedTicket((state) => ({ ...state, isDrawerOpen: false }));
                  toasterStore.push(
                    alert(
                      t('ticket_drawer_v2.success_messages.ticket_deleted'),
                      AlertTypes.success,
                    ),
                  );
                } catch (error) {
                  connection.handleRequestError(
                    error,
                    t('error_messages.tickets.failed_to_delete_ticket'),
                  );
                }
              }}
            />
          )}
        </>
      </Drawer>
    </Box>
  );
});

const GetStateColor = (state: Nullable<TicketState>) => {
  switch (state) {
    case TicketState.VOIDED:
      return 'error';
    case TicketState.APPROVED:
      return 'success';
    default:
      return 'info';
  }
};
